
import { BarsArrowUpIcon, CheckCircleIcon, FunnelIcon, NoSymbolIcon } from '@heroicons/react/24/solid';
import type { CustomFlowbiteTheme, TabsRef } from 'flowbite-react';
import {
  Banner, Spinner, Tabs,
} from 'flowbite-react';
import Moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { Months } from '../../../../common-utils/formatters';
import type { AsyncStatus } from '../../../@types/AsyncStatus';
import type { DPE } from '../../../@types/DPE';
import { DPE_STEPS } from '../../../@types/DPE';
import type { DpeTableData } from '../../../@types/DpeTableData';
import { useAdsModal } from '../../../hooks/useAdsModal';
import {
  resetAll,
  setCurrentStep,
  setDpeClosedData,
  setDpeData,
  setDpeOldClosedData,
  setDpeOldData,
  setDpeOldVoluntarilyUntreatedData,
  setDpeVoluntarilyUntreatedData,
  setMonthFilteredNewDpe,
  setTypeFilteredNewDpe,
} from '../../../store/DpeReducer';
import type { RootState } from '../../../store/store';
import { fetchNewDpes } from '../../../utils/api';
import { DPEDict } from '../../../utils/dictionnayMap';
import { JWTHeader } from '../../../utils/JWTHeader';
import { ROUTES } from '../../../utils/routes';
import AdsModal from '../../common/AdsModal/AdsModal';
import Badge from '../../common/Badge';
import { showToast } from '../../common/Notification';
import WarningValidationPending from '../../common/WarningValidationPending';
import { CoproAffectationToggle, getStoredPreference } from '../components/CoproAffectationToggle';
import DpeDetailsPopup from '../components/DpeDetailsPopup';
import { DPE_QUALIF_STEPS } from '../components/DpeStepsQualif/dpeQualifStep';
import ProspectingOpportunitiesOwnerNavTabs from '../components/ProspectingOpportunitiesOwnerNavTabs';
import { formatDpeData } from '../utils/formatters';
import DpeTable from './components/TableDPEContainer';

const baseHeaders = [
  { title: 'Nom IMMEUBLE', class: '' },
  { title: 'Adresse IMMEUBLE', class: '' },
  { title: 'Caractéristiques à identifier', class: ' bg-[#FFC000]', customContentSelectedClass: ' bg-[#FEFF00]' },
];

// The categories "A traiter", "DPE sans propriétaire", "DPE plus d'un mois", and "DPE sans propriétaire + 1 mois"
// will not show the "Personne en charge de la qualification des DPE" column
const finalHeaders = baseHeaders;

const customTheme: CustomFlowbiteTheme['tabs'] = {
  base: 'flex flex-col',
  tablist: {
    base: 'flex text-center',
    tabitem: {
      base: 'flex items-center justify-center rounded-t-lg p-4 text-sm font-medium first:ml-0 disabled:cursor-not-allowed disabled:text-gray-400 disabled:dark:text-gray-500 w-full md:w-auto',
      variant: {
        default: {
          base: 'text-gray-500 dark:text-gray-400',
          active: {
            on: 'active bg-gray-100 p-4 text-rgpd-blue',
            off: 'bg-white hover:bg-gray-50 hover:text-gray-700 dark:bg-gray-800 dark:hover:bg-gray-700 dark:hover:text-white',
          },
        },
      },
    },
  },
};

function NewDPE() {
  const dispatch = useDispatch();
  const company = useSelector((state: RootState) => state.auth.company);
  const jwt = useSelector((state: RootState) => state.auth.token);
  const dpeData = useSelector((state: RootState) => state.dpe.dpeData);
  const dpeClosedData = useSelector((state: RootState) => state.dpe.dpeClosedData);
  const dpeOldData = useSelector((state: RootState) => state.dpe.dpeOldData);
  const dpeOldClosedData = useSelector((state: RootState) => state.dpe.dpeOldClosedData);
  const dpeVoluntarilyUntreatedData = useSelector((state: RootState) => state.dpe.dpeVoluntarilyUntreatedData);
  const dpeOldVoluntarilyUntreatedData = useSelector((state: RootState) => state.dpe.dpeOldVoluntarilyUntreatedData);
  const monthFiltered = useSelector((state: RootState) => state.dpe.monthFilteredNewDpe);
  const navigate = useNavigate();
  const [statusDpe, setStatusDpe] = useState<AsyncStatus>('IDLE');
  const [statusTreatedDpe, setStatusTreatedDpe] = useState<AsyncStatus>('IDLE');
  const [statusOldDpe, setStatusOldDpe] = useState<AsyncStatus>('IDLE');
  const [statusOldTreatedDpe, setStatusOldTreatedDpe] = useState<AsyncStatus>('IDLE');
  const [statusVoluntarilyUntreatedDpe, setStatusVoluntarilyUntreatedDpe] = useState<AsyncStatus>('IDLE');
  const [statusOldVoluntarilyUntreatedDpe, setStatusOldVoluntarilyUntreatedDpe] = useState<AsyncStatus>('IDLE');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedDpeDetails, setSelectedDpeDetails] = useState<Partial<DPE>>({});
  const isDpeIdentified = useSelector((state: RootState) => state.dpe.isDpeIdentified);
  const currentStep = useSelector((state: RootState) => state.dpe.currentStep);
  const { state } = useLocation();
  const tabsRef = useRef<TabsRef>(null);
  const [paginationState, setPaginationState] = useState({
    dpeNew: { page: 0, rowsPerPage: 10, totalCount: 0 },
    dpeTreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
    dpeOld: { page: 0, rowsPerPage: 10, totalCount: 0 },
    dpeOldTreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
    dpeVoluntarilyUntreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
    dpeOldVoluntarilyUntreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
  });

  type DpeAgeFilter = 'all' | 'less15d';
  const [dpeAgeFilter, setDpeAgeFilter] = useState<DpeAgeFilter>('all');

  const assignedCoprosOnlyRef = useRef(getStoredPreference());

  const { handleAdsClick, adsModalProps } = useAdsModal();

  const getReceivedAtAfterFromFilter = (filter: DpeAgeFilter): string | undefined => {
    if (filter === 'less15d') {
      const d = new Date();
      d.setDate(d.getDate() - 15);
      return d.toISOString().split('T')[0];
    }
    return undefined;
  };

  const handleDetailClick = (dpeDetails: Record<string, unknown>) => {
    const orderedDpeDetails: Record<string, unknown> = {};
    Object.keys(DPEDict).forEach((key) => {
      if (dpeDetails[key]) {
        orderedDpeDetails[key] = dpeDetails[key];
      }
    });
    setSelectedDpeDetails(orderedDpeDetails);
    setIsModalOpen(true);
  };

  interface DpeFetchResponse {
    results: Array<{
      id: number;
      remoteId: string;
      copro: { id: number; name: string };
      details: Record<string, unknown> & {
        banLabel?: string;
        classeBilanDpe?: string;
        dateReceptionDpe?: string;
        complEtageAppartement?: string;
        complRefLogement?: string;
        surfaceHabitableLogement?: string;
        banPostcode?: string;
        banStreet?: string;
        banHousenumber?: string;
        banCity?: string;
        identifiantDpe?: string;
      };
      DPEQualifier?: { firstname: string; lastname?: string };
    }>;
    pagination: { totalResults: number };
  }

  const formatDpeFetch = (dpeResult: DpeFetchResponse) => ({
    data: dpeResult.results.map((item) => {
      if (item.details) {
        const newItem: Partial<DpeTableData> = {};
        newItem.id = item.id;
        newItem.remoteId = item.remoteId;
        newItem.idCopro = item.copro.id;
        newItem['Nom IMMEUBLE'] = item.copro.name;
        newItem.banLabel = item.details.banLabel;
        newItem['Personne en charge de la qualification des DPE'] = item.DPEQualifier
          ? `${item.DPEQualifier.firstname} ${item.DPEQualifier.lastname ? item.DPEQualifier.lastname.toUpperCase() : ''}` : '';
        newItem.value = item.details.classeBilanDpe ? `${item.details.classeBilanDpe} ` : '';
        newItem.date = item.details.dateReceptionDpe ? Moment(item.details.dateReceptionDpe).format('L') : '';
        newItem.floor = item.details.complEtageAppartement;
        newItem['INFO DPE'] = item.details.complRefLogement ? item.details.complRefLogement : '';
        newItem.surface = item.details.surfaceHabitableLogement ? item.details.surfaceHabitableLogement : '';
        newItem.postalCode = item.details.banPostcode ? item.details.banPostcode : '';
        newItem.address = item.details.banStreet ? `${item.details.banHousenumber} ${item.details.banStreet}` : '';
        newItem.city = item.details.banCity ? item.details.banCity : '';
        newItem.details = { ...item.details, remoteId: item.remoteId, id: item.id };
        newItem.visible = false;
        newItem.identifiantDpe = item.details.identifiantDpe ? item.details.identifiantDpe : 'N/A';
        return newItem as DpeTableData;
      }
      return null;
    }).filter((item) => item !== null),
    totalCount: dpeResult.pagination.totalResults,
  });

  const fetchDpeDatas = (
    fetchDpeNewUri?: string, 
    fetchDpeTreatedUri?: string,
    fetchDpeOldUri?: string,
    fetchDpeOldTreatedUri?: string,
    fetchDpeVoluntarilyUntreatedUri?: string,
    fetchDpeOldVoluntarilyUntreatedUri?: string,
  ) => {
    if (fetchDpeNewUri) {
      setStatusDpe('PENDING');
      fetch(fetchDpeNewUri, JWTHeader(jwt))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Internal server error');
          }
          return response.json();
        })
        .then((result) => {
          const { data, totalCount } = formatDpeFetch(result);
          dispatch(setDpeData(data.map(({ 'Nouveau DPE à identifier': _, ...rest }) => rest)));
          setPaginationState(prev => ({
            ...prev,
            dpeNew: { ...prev.dpeNew, totalCount },
          }));
          setStatusDpe('SUCCESS');
        })
        .catch((response_error) => {
          console.error('Error fetching company dpes :', response_error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
          setStatusDpe('ERROR');
        });
    }

    if (fetchDpeTreatedUri) {
      setStatusTreatedDpe('PENDING');
      fetch(fetchDpeTreatedUri, JWTHeader(jwt))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Internal server error');
          }
          return response.json();
        })
        .then((result) => {
          const { data, totalCount } = formatDpeFetch(result);
          dispatch(setDpeClosedData(data.map(({ 'Nouveau DPE à identifier': _, ...rest }) => rest)));
          setPaginationState(prev => ({
            ...prev,
            dpeTreated: { ...prev.dpeTreated, totalCount },
          }));
          setStatusTreatedDpe('SUCCESS');
        })
        .catch((response_error) => {
          console.error('Error fetching company dpes :', response_error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
          setStatusTreatedDpe('ERROR');
        });
    }

    if (fetchDpeOldUri) {
      setStatusOldDpe('PENDING');
      fetch(fetchDpeOldUri, JWTHeader(jwt))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Internal server error');
          }
          return response.json();
        })
        .then((result) => {
          const { data, totalCount } = formatDpeFetch(result);
          dispatch(setDpeOldData(data.map(({ 'Nouveau DPE à identifier': _, ...rest }) => rest)));
          setPaginationState(prev => ({
            ...prev,
            dpeOld: { ...prev.dpeOld, totalCount },
          }));
          setStatusOldDpe('SUCCESS');
        })
        .catch((response_error) => {
          console.error('Error fetching company old dpes :', response_error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
          setStatusOldDpe('ERROR');
        });
    }

    if (fetchDpeOldTreatedUri) {
      setStatusOldTreatedDpe('PENDING');
      fetch(fetchDpeOldTreatedUri, JWTHeader(jwt))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Internal server error');
          }
          return response.json();
        })
        .then((result) => {
          const { data, totalCount } = formatDpeFetch(result);
          dispatch(setDpeOldClosedData(data.map(({ 'Nouveau DPE à identifier': _, ...rest }) => rest)));
          setPaginationState(prev => ({
            ...prev,
            dpeOldTreated: { ...prev.dpeOldTreated, totalCount },
          }));
          setStatusOldTreatedDpe('SUCCESS');
        })
        .catch((response_error) => {
          console.error('Error fetching company old treated dpes :', response_error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
          setStatusOldTreatedDpe('ERROR');
        });
    }

    if (fetchDpeVoluntarilyUntreatedUri) {
      setStatusVoluntarilyUntreatedDpe('PENDING');
      fetch(fetchDpeVoluntarilyUntreatedUri, JWTHeader(jwt))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Internal server error');
          }
          return response.json();
        })
        .then((result) => {
          const { data, totalCount } = formatDpeFetch(result);
          dispatch(setDpeVoluntarilyUntreatedData(data.map(({ 'Nouveau DPE à identifier': _, ...rest }) => rest)));
          setPaginationState(prev => ({
            ...prev,
            dpeVoluntarilyUntreated: { ...prev.dpeVoluntarilyUntreated, totalCount },
          }));
          setStatusVoluntarilyUntreatedDpe('SUCCESS');
        })
        .catch((response_error) => {
          console.error('Error fetching company voluntarily untreated dpes :', response_error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
          setStatusVoluntarilyUntreatedDpe('ERROR');
        });
    }

    if (fetchDpeOldVoluntarilyUntreatedUri) {
      setStatusOldVoluntarilyUntreatedDpe('PENDING');
      fetch(fetchDpeOldVoluntarilyUntreatedUri, JWTHeader(jwt))
        .then((response) => {
          if (!response.ok) {
            throw new Error('Internal server error');
          }
          return response.json();
        })
        .then((result) => {
          const { data, totalCount } = formatDpeFetch(result);
          dispatch(setDpeOldVoluntarilyUntreatedData(data.map(({ 'Nouveau DPE à identifier': _, ...rest }) => rest)));
          setPaginationState(prev => ({
            ...prev,
            dpeOldVoluntarilyUntreated: { ...prev.dpeOldVoluntarilyUntreated, totalCount },
          }));
          setStatusOldVoluntarilyUntreatedDpe('SUCCESS');
        })
        .catch((response_error) => {
          console.error('Error fetching company old voluntarily untreated dpes :', response_error);
          showToast('error', 'Une erreur est survenue lors du chargement des données.');
          setStatusOldVoluntarilyUntreatedDpe('ERROR');
        });
    }
  };

  const handlePageChange = (type: 'dpeNew' | 'dpeTreated' | 'dpeOld' | 'dpeOldTreated' | 'dpeVoluntarilyUntreated' | 'dpeOldVoluntarilyUntreated') => (page: number, rowsPerPage: number) => {
    const currentRowsPerPage = paginationState[type].rowsPerPage;
    const effectivePage = currentRowsPerPage !== rowsPerPage ? 0 : page;
    setPaginationState(prev => ({
      ...prev,
      [type]: { ...prev[type], page: effectivePage, rowsPerPage },
    }));

    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const oneMonthAgoStr = oneMonthAgo.toISOString().split('T')[0];
    const receivedAfter = getReceivedAtAfterFromFilter(dpeAgeFilter);

    const affectation = assignedCoprosOnlyRef.current;

    if (type === 'dpeNew') {
      const fetchDpeNewUri = monthFiltered && monthFiltered.date
        ? fetchNewDpes(company.id, DPE_STEPS.NEW, monthFiltered.date, monthFiltered.type, effectivePage + 1, rowsPerPage, null, oneMonthAgoStr, receivedAfter, affectation)
        : fetchNewDpes(company.id, DPE_STEPS.NEW, null, null, effectivePage + 1, rowsPerPage, null, oneMonthAgoStr, receivedAfter, affectation);
      fetchDpeDatas(fetchDpeNewUri, undefined, undefined, undefined, undefined, undefined);
    } else if (type === 'dpeTreated') {
      const fetchDpeTreatedUri = monthFiltered && monthFiltered.date
        ? fetchNewDpes(company.id, DPE_STEPS.NO_OWNER, monthFiltered.date, monthFiltered.type, effectivePage + 1, rowsPerPage, null, oneMonthAgoStr, receivedAfter, affectation)
        : fetchNewDpes(company.id, DPE_STEPS.NO_OWNER, null, null, effectivePage + 1, rowsPerPage, null, oneMonthAgoStr, receivedAfter, affectation);
      fetchDpeDatas(undefined, fetchDpeTreatedUri, undefined, undefined, undefined, undefined);
    } else if (type === 'dpeOld') {
      const fetchDpeOldUri = monthFiltered && monthFiltered.date
        ? fetchNewDpes(company.id, DPE_STEPS.NEW, monthFiltered.date, monthFiltered.type, effectivePage + 1, rowsPerPage, oneMonthAgoStr, null, receivedAfter, affectation)
        : fetchNewDpes(company.id, DPE_STEPS.NEW, null, null, effectivePage + 1, rowsPerPage, oneMonthAgoStr, null, receivedAfter, affectation);
      fetchDpeDatas(undefined, undefined, fetchDpeOldUri, undefined, undefined, undefined);
    } else if (type === 'dpeOldTreated') {
      const fetchDpeOldTreatedUri = monthFiltered && monthFiltered.date
        ? fetchNewDpes(company.id, DPE_STEPS.NO_OWNER, monthFiltered.date, monthFiltered.type, effectivePage + 1, rowsPerPage, oneMonthAgoStr, null, receivedAfter, affectation)
        : fetchNewDpes(company.id, DPE_STEPS.NO_OWNER, null, null, effectivePage + 1, rowsPerPage, oneMonthAgoStr, null, receivedAfter, affectation);
      fetchDpeDatas(undefined, undefined, undefined, fetchDpeOldTreatedUri, undefined, undefined);
    } else if (type === 'dpeVoluntarilyUntreated') {
      const fetchDpeVoluntarilyUntreatedUri = monthFiltered && monthFiltered.date
        ? fetchNewDpes(company.id, DPE_STEPS.VOLUNTARILY_UNTREATED, monthFiltered.date, monthFiltered.type, effectivePage + 1, rowsPerPage, null, oneMonthAgoStr, receivedAfter, affectation)
        : fetchNewDpes(company.id, DPE_STEPS.VOLUNTARILY_UNTREATED, null, null, effectivePage + 1, rowsPerPage, null, oneMonthAgoStr, receivedAfter, affectation);
      fetchDpeDatas(undefined, undefined, undefined, undefined, fetchDpeVoluntarilyUntreatedUri, undefined);
    } else if (type === 'dpeOldVoluntarilyUntreated') {
      const fetchDpeOldVoluntarilyUntreatedUri = monthFiltered && monthFiltered.date
        ? fetchNewDpes(company.id, DPE_STEPS.VOLUNTARILY_UNTREATED, monthFiltered.date, monthFiltered.type, effectivePage + 1, rowsPerPage, oneMonthAgoStr, null, receivedAfter, affectation)
        : fetchNewDpes(company.id, DPE_STEPS.VOLUNTARILY_UNTREATED, null, null, effectivePage + 1, rowsPerPage, oneMonthAgoStr, null, receivedAfter, affectation);
      fetchDpeDatas(undefined, undefined, undefined, undefined, undefined, fetchDpeOldVoluntarilyUntreatedUri);
    }
  };

  const handleFetchDpeDatas = (filterOverride?: DpeAgeFilter) => {
    const oneMonthAgo = new Date();
    oneMonthAgo.setMonth(oneMonthAgo.getMonth() - 1);
    const oneMonthAgoStr = oneMonthAgo.toISOString().split('T')[0];
    const receivedAfter = getReceivedAtAfterFromFilter(filterOverride ?? dpeAgeFilter);
    const affectation = assignedCoprosOnlyRef.current;

    const fetchDpeNewUri = monthFiltered && monthFiltered.date
      ? fetchNewDpes(
        company.id,
        DPE_STEPS.NEW,
        monthFiltered.date,
        monthFiltered.type,
        paginationState.dpeNew.page + 1,
        paginationState.dpeNew.rowsPerPage,
        null,
        oneMonthAgoStr,
        receivedAfter,
        affectation,
      )
      : fetchNewDpes(
        company.id,
        DPE_STEPS.NEW,
        null,
        null,
        paginationState.dpeNew.page + 1,
        paginationState.dpeNew.rowsPerPage,
        null,
        oneMonthAgoStr,
        receivedAfter,
        affectation,
      );

    const fetchDpeTreatedUri = monthFiltered && monthFiltered.date
      ? fetchNewDpes(
        company.id,
        DPE_STEPS.NO_OWNER,
        monthFiltered.date,
        monthFiltered.type,
        paginationState.dpeTreated.page + 1,
        paginationState.dpeTreated.rowsPerPage,
        null,
        oneMonthAgoStr,
        receivedAfter,
        affectation,
      )
      : fetchNewDpes(
        company.id,
        DPE_STEPS.NO_OWNER,
        null,
        null,
        paginationState.dpeTreated.page + 1,
        paginationState.dpeTreated.rowsPerPage,
        null,
        oneMonthAgoStr,
        receivedAfter,
        affectation,
      );

    const fetchDpeOldUri = monthFiltered && monthFiltered.date
      ? fetchNewDpes(
        company.id,
        DPE_STEPS.NEW,
        monthFiltered.date,
        monthFiltered.type,
        paginationState.dpeOld.page + 1,
        paginationState.dpeOld.rowsPerPage,
        oneMonthAgoStr,
        null,
        receivedAfter,
        affectation,
      )
      : fetchNewDpes(
        company.id,
        DPE_STEPS.NEW,
        null,
        null,
        paginationState.dpeOld.page + 1,
        paginationState.dpeOld.rowsPerPage,
        oneMonthAgoStr,
        null,
        receivedAfter,
        affectation,
      );

    const fetchDpeOldTreatedUri = monthFiltered && monthFiltered.date
      ? fetchNewDpes(
        company.id,
        DPE_STEPS.NO_OWNER,
        monthFiltered.date,
        monthFiltered.type,
        paginationState.dpeOldTreated.page + 1,
        paginationState.dpeOldTreated.rowsPerPage,
        oneMonthAgoStr,
        null,
        receivedAfter,
        affectation,
      )
      : fetchNewDpes(
        company.id,
        DPE_STEPS.NO_OWNER,
        null,
        null,
        paginationState.dpeOldTreated.page + 1,
        paginationState.dpeOldTreated.rowsPerPage,
        oneMonthAgoStr,
        null,
        receivedAfter,
        affectation,
      );

    const fetchDpeVoluntarilyUntreatedUri = monthFiltered && monthFiltered.date
      ? fetchNewDpes(
        company.id,
        DPE_STEPS.VOLUNTARILY_UNTREATED,
        monthFiltered.date,
        monthFiltered.type,
        paginationState.dpeVoluntarilyUntreated.page + 1,
        paginationState.dpeVoluntarilyUntreated.rowsPerPage,
        null,
        oneMonthAgoStr,
        receivedAfter,
        affectation,
      )
      : fetchNewDpes(
        company.id,
        DPE_STEPS.VOLUNTARILY_UNTREATED,
        null,
        null,
        paginationState.dpeVoluntarilyUntreated.page + 1,
        paginationState.dpeVoluntarilyUntreated.rowsPerPage,
        null,
        oneMonthAgoStr,
        receivedAfter,
        affectation,
      );

    const fetchDpeOldVoluntarilyUntreatedUri = monthFiltered && monthFiltered.date
      ? fetchNewDpes(
        company.id,
        DPE_STEPS.VOLUNTARILY_UNTREATED,
        monthFiltered.date,
        monthFiltered.type,
        paginationState.dpeOldVoluntarilyUntreated.page + 1,
        paginationState.dpeOldVoluntarilyUntreated.rowsPerPage,
        oneMonthAgoStr,
        null,
        receivedAfter,
        affectation,
      )
      : fetchNewDpes(
        company.id,
        DPE_STEPS.VOLUNTARILY_UNTREATED,
        null,
        null,
        paginationState.dpeOldVoluntarilyUntreated.page + 1,
        paginationState.dpeOldVoluntarilyUntreated.rowsPerPage,
        oneMonthAgoStr,
        null,
        receivedAfter,
        affectation,
      );

    fetchDpeDatas(fetchDpeNewUri, fetchDpeTreatedUri, fetchDpeOldUri, fetchDpeOldTreatedUri, fetchDpeVoluntarilyUntreatedUri, fetchDpeOldVoluntarilyUntreatedUri);
  };

  const handleDpeAgeFilterChange = (filter: DpeAgeFilter) => {
    setDpeAgeFilter(filter);
    setPaginationState({
      dpeNew: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeTreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeOld: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeOldTreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeVoluntarilyUntreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeOldVoluntarilyUntreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
    });
    dispatch(resetAll());
    dispatch(setCurrentStep(DPE_QUALIF_STEPS.COMPLETING_OWNER_COUNT));
    handleFetchDpeDatas(filter);
  };

  const handleCoproFilterChange = (assignedCoprosOnly: boolean) => {
    assignedCoprosOnlyRef.current = assignedCoprosOnly;
    setPaginationState({
      dpeNew: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeTreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeOld: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeOldTreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeVoluntarilyUntreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
      dpeOldVoluntarilyUntreated: { page: 0, rowsPerPage: 10, totalCount: 0 },
    });
    dispatch(resetAll());
    dispatch(setCurrentStep(DPE_QUALIF_STEPS.COMPLETING_OWNER_COUNT));
    handleFetchDpeDatas();
  };

  const handleDisableFiltering = () => {
    dispatch(setMonthFilteredNewDpe(null));
    navigate(ROUTES.PROSPECTING_NEW_OPPORTUNITIES, { replace: true, state: {} });
  };

  useEffect(() => {
    if (state && state.filterMonth) {
      if (state.showNoOwner) {
        navigate(ROUTES.PROSPECTING_NEW_OPPORTUNITIES, { replace: true, state: { showNoOwner: state.showNoOwner } });
      } else {
        navigate(ROUTES.PROSPECTING_NEW_OPPORTUNITIES, { replace: true, state: {} });
      }
      dispatch(setTypeFilteredNewDpe('reception'));
      dispatch(setMonthFilteredNewDpe(state.filterMonth));
    }
  }, [state, dispatch, navigate]);

  useEffect(() => {
    if (!company || !jwt || !company.id ) {return;}
    if (state && state.filterMonth) {return;}

    if (state && state.showNoOwner) {
      tabsRef.current?.setActiveTab(1); // open traité tab
    } else {
      tabsRef.current?.setActiveTab(0); // open à traiter tab
    }

    dispatch(resetAll());
    dispatch(setCurrentStep(DPE_QUALIF_STEPS.COMPLETING_OWNER_COUNT));

    handleFetchDpeDatas();
  }, [jwt, company, isDpeIdentified, monthFiltered, dispatch, state]);

  const displayedDPEData = formatDpeData(dpeData, handleDetailClick, handleAdsClick);
  const displayedDPEClosedData = formatDpeData(dpeClosedData, handleDetailClick, handleAdsClick);
  const displayedDPEOldData = formatDpeData(dpeOldData, handleDetailClick, handleAdsClick);
  const displayedDPEOldClosedData = formatDpeData(dpeOldClosedData, handleDetailClick, handleAdsClick);
  const displayedDPEVoluntarilyUntreatedData = formatDpeData(dpeVoluntarilyUntreatedData, handleDetailClick, handleAdsClick);
  const displayedDPEOldVoluntarilyUntreatedData = formatDpeData(dpeOldVoluntarilyUntreatedData, handleDetailClick, handleAdsClick);

  return (
    <div className="mb-8 overflow-x-scroll rounded-lg bg-white p-4 shadow-md">
      <div className="mb-2 border-b border-gray-200 pb-4">
        <h2 className="text-xl font-semibold text-gray-900">Nouvelles opportunités (DPE)</h2>
        <p className="mt-1 text-sm text-gray-600">Découvrez les dernières opportunités de locations/ventes liées aux DPEs</p>
      </div>
      <ProspectingOpportunitiesOwnerNavTabs />
      <WarningValidationPending />
      <div className="mb-4 flex flex-wrap items-center gap-4">
        <CoproAffectationToggle onFilterChange={handleCoproFilterChange} />
        <FunnelIcon className="h-4 w-4 text-gray-500" />
        <span className="text-sm font-medium text-gray-600">Ancienneté DPE :</span>
        <div className="flex gap-2">
          <button
            onClick={() => handleDpeAgeFilterChange('all')}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 ${
              dpeAgeFilter === 'all'
                ? 'bg-rgpd-blue text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Tous
          </button>
          <button
            onClick={() => handleDpeAgeFilterChange('less15d')}
            className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-200 ${
              dpeAgeFilter === 'less15d'
                ? 'bg-rgpd-blue text-white shadow-sm'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            Moins de 15j
          </button>
        </div>
      </div>
      {monthFiltered && monthFiltered.date && (
        <Banner className="mb-2">
          <div className="flex w-full flex-col justify-between border-b border-gray-200 bg-gray-50 p-4 md:flex-row dark:border-gray-600 dark:bg-gray-700">
            <div className="mb-4 md:mr-4 md:mb-0">
              <h2 className="mb-1 text-base font-semibold text-gray-900 dark:text-white">
                Filtre{' '}
                {`${Months[(new Date(monthFiltered.date).getMonth())]} ${(new Date(monthFiltered.date).getFullYear()).toString()}`}
                {' '}
                par mois{' '}
                {monthFiltered.type == 'reception' ? 'de réception dans l\'interface' : 'd\'établissement'}
              </h2>
              <p className="flex items-center text-sm font-normal text-gray-500 dark:text-gray-400">
                Vous avez filtré les données pour le mois de
                {' '}
                {`${Months[(new Date(monthFiltered.date).getMonth())]} ${(new Date(monthFiltered.date).getFullYear()).toString()}`}
                . Les données affichées ci-dessous sont celles du mois sélectionné.
              </p>
            </div>
            <button onClick={handleDisableFiltering} className="mr-2 inline-flex items-center justify-center rounded-lg bg-rgpd-blue px-3 py-1 text-xs font-medium text-white hover:bg-rgpd-blue-dark focus:ring-4 focus:outline-none">
              Désactiver le filtre
            </button>
          </div>
        </Banner>
      )}
      <div className="rounded-lg bg-white shadow">
        <Tabs
          aria-label="Default tabs"
          className="rounded"
          theme={customTheme}
          ref={tabsRef}
          onActiveTabChange={() => {
            dispatch(resetAll());

            if (currentStep === DPE_QUALIF_STEPS.REDIRECTION_TO_DPE_OR_IDENTIFIED) {
              handleFetchDpeDatas();
            }
          }}
        >
          <Tabs.Item title={`A traiter ${displayedDPEData ? `(${paginationState.dpeNew.totalCount})` : ''}`} active icon={BarsArrowUpIcon} color="text-rgpd-blue">
            <div className="p-4">
              {statusDpe === 'PENDING' && (
                <p className="mx-10 my-32 text-center">
                  <Spinner aria-label="Center-aligned spinner" size="xl" />
                </p>
              )}
              {statusDpe === 'ERROR'
                  && (
                    <div className="mx-10 my-32 text-center">
                      <Badge
                        mode="error"
                        text="Une erreur est survenue lors du chargement de vos données"
                      />
                    </div>
                  )}
              {statusDpe === 'SUCCESS' && displayedDPEData && displayedDPEData.length > 0 && (
                <DpeTable 
                  data={displayedDPEData} 
                  headers={finalHeaders} 
                  isDpeTreated={false} 
                  tableClass="text-left" 
                  apiPagination={true}
                  totalCount={paginationState.dpeNew.totalCount}
                  onPageChange={handlePageChange('dpeNew')}
                  currentPage={paginationState.dpeNew.page}
                  currentRowsPerPage={paginationState.dpeNew.rowsPerPage}
                />
              )}
              {statusDpe === 'SUCCESS' && displayedDPEData && displayedDPEData.length === 0 && (
                <div className="w-full text-center">
                  <Badge mode="warning" text="Aucun DPE trouvé" className="mx-10 my-32" />
                </div>
              )}
            </div>
          </Tabs.Item>
          <Tabs.Item title={`DPE sans propriétaire trouvé ${displayedDPEClosedData ? `(${paginationState.dpeTreated.totalCount})` : ''}`} icon={CheckCircleIcon}>
            <div className="p-4">
              {statusTreatedDpe === 'PENDING' && (
                <p className="mx-10 my-32 text-center">
                  <Spinner aria-label="Center-aligned spinner" size="xl" />
                </p>
              )}
              {statusTreatedDpe === 'ERROR'
                  && (
                    <div className="w-full text-center">
                      <Badge
                        mode="error"
                        text="Une erreur est survenue au chargement de vos données"
                        className="mx-10 my-32"
                      />
                    </div>
                  )}
              {statusTreatedDpe === 'SUCCESS' && displayedDPEClosedData && displayedDPEClosedData.length > 0 && (
                <DpeTable 
                  data={displayedDPEClosedData} 
                  headers={finalHeaders} 
                  isDpeTreated 
                  tableClass="text-left" 
                  apiPagination={true}
                  totalCount={paginationState.dpeTreated.totalCount}
                  onPageChange={handlePageChange('dpeTreated')}
                  currentPage={paginationState.dpeTreated.page}
                  currentRowsPerPage={paginationState.dpeTreated.rowsPerPage}
                />
              )}
              {statusTreatedDpe === 'SUCCESS' && displayedDPEClosedData && displayedDPEClosedData.length === 0 && (
                <div className="w-full text-center">
                  <Badge mode="warning" text="Aucun DPE trouvé" className="mx-10 my-32" />
                </div>
              )}
            </div>
          </Tabs.Item>
          <Tabs.Item title={`DPE non traité de plus d'un mois ${displayedDPEOldData ? `(${paginationState.dpeOld.totalCount})` : ''}`} icon={BarsArrowUpIcon}>
            <div className="p-4">
              {statusOldDpe === 'PENDING' && (
                <p className="mx-10 my-32 text-center">
                  <Spinner aria-label="Center-aligned spinner" size="xl" />
                </p>
              )}
              {statusOldDpe === 'ERROR'
                  && (
                    <div className="w-full text-center">
                      <Badge
                        mode="error"
                        text="Une erreur est survenue au chargement de vos données"
                        className="mx-10 my-32"
                      />
                    </div>
                  )}
              {statusOldDpe === 'SUCCESS' && displayedDPEOldData && displayedDPEOldData.length > 0 && (
                <DpeTable 
                  data={displayedDPEOldData} 
                  headers={finalHeaders} 
                  isDpeTreated={false} 
                  isOldDpe={true}
                  tableClass="text-left" 
                  apiPagination={true}
                  totalCount={paginationState.dpeOld.totalCount}
                  onPageChange={handlePageChange('dpeOld')}
                  currentPage={paginationState.dpeOld.page}
                  currentRowsPerPage={paginationState.dpeOld.rowsPerPage}
                />
              )}
              {statusOldDpe === 'SUCCESS' && displayedDPEOldData && displayedDPEOldData.length === 0 && (
                <div className="w-full text-center">
                  <Badge mode="warning" text="Aucun DPE trouvé" className="mx-10 my-32" />
                </div>
              )}
            </div>
          </Tabs.Item>
          <Tabs.Item title={`DPE sans propriétaire trouvé de plus d'un mois ${displayedDPEOldClosedData ? `(${paginationState.dpeOldTreated.totalCount})` : ''}`} icon={CheckCircleIcon}>
            <div className="p-4">
              {statusOldTreatedDpe === 'PENDING' && (
                <p className="mx-10 my-32 text-center">
                  <Spinner aria-label="Center-aligned spinner" size="xl" />
                </p>
              )}
              {statusOldTreatedDpe === 'ERROR'
                  && (
                    <div className="w-full text-center">
                      <Badge
                        mode="error"
                        text="Une erreur est survenue au chargement de vos données"
                        className="mx-10 my-32"
                      />
                    </div>
                  )}
              {statusOldTreatedDpe === 'SUCCESS' && displayedDPEOldClosedData && displayedDPEOldClosedData.length > 0 && (
                <DpeTable 
                  data={displayedDPEOldClosedData} 
                  headers={finalHeaders} 
                  isDpeTreated 
                  isOldDpe={true}
                  tableClass="text-left" 
                  apiPagination={true}
                  totalCount={paginationState.dpeOldTreated.totalCount}
                  onPageChange={handlePageChange('dpeOldTreated')}
                  currentPage={paginationState.dpeOldTreated.page}
                  currentRowsPerPage={paginationState.dpeOldTreated.rowsPerPage}
                />
              )}
              {statusOldTreatedDpe === 'SUCCESS' && displayedDPEOldClosedData && displayedDPEOldClosedData.length === 0 && (
                <div className="w-full text-center">
                  <Badge mode="warning" text="Aucun DPE trouvé" className="mx-10 my-32" />
                </div>
              )}
            </div>
          </Tabs.Item>
          <Tabs.Item title={`DPE non traité volontairement ${displayedDPEVoluntarilyUntreatedData ? `(${paginationState.dpeVoluntarilyUntreated.totalCount})` : ''}`} icon={NoSymbolIcon}>
            <div className="p-4">
              {statusVoluntarilyUntreatedDpe === 'PENDING' && (
                <p className="mx-10 my-32 text-center">
                  <Spinner aria-label="Center-aligned spinner" size="xl" />
                </p>
              )}
              {statusVoluntarilyUntreatedDpe === 'ERROR'
                  && (
                    <div className="w-full text-center">
                      <Badge
                        mode="error"
                        text="Une erreur est survenue au chargement de vos données"
                        className="mx-10 my-32"
                      />
                    </div>
                  )}
              {statusVoluntarilyUntreatedDpe === 'SUCCESS' && displayedDPEVoluntarilyUntreatedData && displayedDPEVoluntarilyUntreatedData.length > 0 && (
                <DpeTable 
                  data={displayedDPEVoluntarilyUntreatedData} 
                  headers={finalHeaders} 
                  isDpeTreated 
                  tableClass="text-left" 
                  apiPagination={true}
                  totalCount={paginationState.dpeVoluntarilyUntreated.totalCount}
                  onPageChange={handlePageChange('dpeVoluntarilyUntreated')}
                  currentPage={paginationState.dpeVoluntarilyUntreated.page}
                  currentRowsPerPage={paginationState.dpeVoluntarilyUntreated.rowsPerPage}
                />
              )}
              {statusVoluntarilyUntreatedDpe === 'SUCCESS' && displayedDPEVoluntarilyUntreatedData && displayedDPEVoluntarilyUntreatedData.length === 0 && (
                <div className="w-full text-center">
                  <Badge mode="warning" text="Aucun DPE trouvé" className="mx-10 my-32" />
                </div>
              )}
            </div>
          </Tabs.Item>
          <Tabs.Item title={`DPE non traité volontairement de plus d'un mois ${displayedDPEOldVoluntarilyUntreatedData ? `(${paginationState.dpeOldVoluntarilyUntreated.totalCount})` : ''}`} icon={NoSymbolIcon}>
            <div className="p-4">
              {statusOldVoluntarilyUntreatedDpe === 'PENDING' && (
                <p className="mx-10 my-32 text-center">
                  <Spinner aria-label="Center-aligned spinner" size="xl" />
                </p>
              )}
              {statusOldVoluntarilyUntreatedDpe === 'ERROR'
                  && (
                    <div className="w-full text-center">
                      <Badge
                        mode="error"
                        text="Une erreur est survenue au chargement de vos données"
                        className="mx-10 my-32"
                      />
                    </div>
                  )}
              {statusOldVoluntarilyUntreatedDpe === 'SUCCESS' && displayedDPEOldVoluntarilyUntreatedData && displayedDPEOldVoluntarilyUntreatedData.length > 0 && (
                <DpeTable 
                  data={displayedDPEOldVoluntarilyUntreatedData} 
                  headers={finalHeaders} 
                  isDpeTreated 
                  isOldDpe={true}
                  tableClass="text-left" 
                  apiPagination={true}
                  totalCount={paginationState.dpeOldVoluntarilyUntreated.totalCount}
                  onPageChange={handlePageChange('dpeOldVoluntarilyUntreated')}
                  currentPage={paginationState.dpeOldVoluntarilyUntreated.page}
                  currentRowsPerPage={paginationState.dpeOldVoluntarilyUntreated.rowsPerPage}
                />
              )}
              {statusOldVoluntarilyUntreatedDpe === 'SUCCESS' && displayedDPEOldVoluntarilyUntreatedData && displayedDPEOldVoluntarilyUntreatedData.length === 0 && (
                <div className="w-full text-center">
                  <Badge mode="warning" text="Aucun DPE trouvé" className="mx-10 my-32" />
                </div>
              )}
            </div>
          </Tabs.Item>
        </Tabs>
      </div>

      <DpeDetailsPopup
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        dpeDetails={selectedDpeDetails}
      />
      <AdsModal {...adsModalProps} />
    </div>
  );
}

export default NewDPE;